import { useState } from "react";
import { Link as RouterLink } from "@tanstack/react-router";
// Hooks
import { useProtectedContent } from "../hooks/useProtectedContent";
import { useBulkOrderMode } from "../hooks/useAuthorisation";
// Components
import { ComponentAuthorisation } from "./ComponentAuthorisation";
// MUI
import GlobalStyles from "@mui/material/GlobalStyles";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton, {
  listItemButtonClasses,
} from "@mui/material/ListItemButton";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { closeSidebar } from "../utils/Utils";
import { getDraftBasePath } from "../utils/orderPaths";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutlined";
import PeopleIcon from "@mui/icons-material/People";
import GroupsIcon from "@mui/icons-material/Groups";

import CheckroomIcon from "@mui/icons-material/Checkroom";
import ListIcon from "@mui/icons-material/List";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import DescriptionIcon from "@mui/icons-material/Description";

import StackedBarChartIcon from "@mui/icons-material/StackedBarChart";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import SsidChartIcon from "@mui/icons-material/SsidChart";

import SupportRoundedIcon from "@mui/icons-material/SupportRounded";
import UndoIcon from "@mui/icons-material/Undo";
import RefreshIcon from "@mui/icons-material/Refresh";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import SubjectIcon from "@mui/icons-material/Subject";
import ChecklistIcon from "@mui/icons-material/Checklist";

import { grey } from "@mui/material/colors";
const darkGrey = grey[700];

import { useColorScheme } from "@mui/material/styles";
import {
  useSidebar,
  SIDEBAR_MINI_WIDTH,
  SIDEBAR_WIDTH,
} from "../contexts/SidebarContext";

// Collapsed, a row stacks its icon over a caption rather than sitting beside a
// label, which is what keeps the rail readable at 90px.
const miniButtonSx = {
  flexDirection: "column",
  justifyContent: "center",
  gap: 0.25,
  px: 0.5,
  py: 0.5,
};

const MiniLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontSize: 10,
      fontWeight: 500,
      lineHeight: 1.2,
      textAlign: "center",
      width: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Typography>
);

export default function Sidebar() {
  const { mode, systemMode } = useColorScheme();
  const isDarkTheme = (systemMode ?? mode) === "dark";
  const { mini, expand } = useSidebar();
  const bulkOrderMode = useBulkOrderMode();
  const draftEntryPath = getDraftBasePath(bulkOrderMode);

  const canCreateStaff = useProtectedContent("createStaff");

  const [openOrders, setOpenOrders] = useState(false);
  const [openStaff, setOpenStaff] = useState(false);
  const [openFinance, setOpenFinance] = useState(false);
  const [openWardrobe, setOpenWardrobe] = useState(false);
  const [openReports, setOpenReports] = useState(false);
  const [openReturns, setOpenReturns] = useState(false);
  const [openEntitlement, setOpenEntitlement] = useState(false);
  const [openRequests, setOpenRequests] = useState(false);
  const [openApprovals, setOpenApprovals] = useState(false);

  const handleClickOrders = () => {
    setOpenOrders(!openOrders);
    setOpenStaff(false);
    setOpenFinance(false);
    setOpenWardrobe(false);
    setOpenReports(false);
    setOpenReturns(false);
    setOpenEntitlement(false);
    setOpenRequests(false);
    setOpenApprovals(false);
  };
  const handleClickStaff = () => {
    setOpenStaff(!openStaff);
    setOpenOrders(false);
    setOpenFinance(false);
    setOpenWardrobe(false);
    setOpenReports(false);
    setOpenReturns(false);
    setOpenEntitlement(false);
    setOpenRequests(false);
    setOpenApprovals(false);
  };
  const handleClickFinance = () => {
    setOpenFinance(!openFinance);
    setOpenStaff(false);
    setOpenOrders(false);
    setOpenWardrobe(false);
    setOpenReports(false);
    setOpenReturns(false);
    setOpenEntitlement(false);
    setOpenRequests(false);
    setOpenApprovals(false);
  };
  const handleClickWardrobe = () => {
    setOpenWardrobe(!openWardrobe);
    setOpenStaff(false);
    setOpenFinance(false);
    setOpenOrders(false);
    setOpenReports(false);
    setOpenReturns(false);
    setOpenEntitlement(false);
    setOpenRequests(false);
    setOpenApprovals(false);
  };
  const handleClickEntitlement = () => {
    setOpenEntitlement(!openEntitlement);
    setOpenStaff(false);
    setOpenFinance(false);
    setOpenOrders(false);
    setOpenReports(false);
    setOpenWardrobe(false);
    setOpenReturns(false);
    setOpenRequests(false);
    setOpenApprovals(false);
  };
  const handleClickReturns = () => {
    setOpenReturns(!openReturns);
    setOpenStaff(false);
    setOpenFinance(false);
    setOpenOrders(false);
    setOpenWardrobe(false);
    setOpenReports(false);
    setOpenEntitlement(false);
    setOpenRequests(false);
    setOpenApprovals(false);
  };
  const handleClickReports = () => {
    setOpenReports(!openReports);
    setOpenStaff(false);
    setOpenFinance(false);
    setOpenOrders(false);
    setOpenWardrobe(false);
    setOpenReturns(false);
    setOpenEntitlement(false);
    setOpenRequests(false);
    setOpenApprovals(false);
  };
  const handleClickRequests = () => {
    setOpenRequests(!openRequests);
    setOpenStaff(false);
    setOpenFinance(false);
    setOpenOrders(false);
    setOpenWardrobe(false);
    setOpenReturns(false);
    setOpenEntitlement(false);
    setOpenReports(false);
    setOpenApprovals(false);
  };

  const handleClickApprovals = () => {
    setOpenApprovals(!openApprovals);
    setOpenStaff(false);
    setOpenFinance(false);
    setOpenOrders(false);
    setOpenWardrobe(false);
    setOpenReturns(false);
    setOpenEntitlement(false);
    setOpenReports(false);
    setOpenRequests(false);
  };

  function RouterListItemButton(props) {
    const { icon, name, to, sxStyle } = props;
    return (
      <ListItemButton
        // selected
        component={RouterLink}
        to={to}
        sx={{
          ...(mini ? miniButtonSx : { px: 1, py: 0.5, ...sxStyle }),
          color: isDarkTheme ? "white" : darkGrey,
          [`&.active`]: {
            color: "primary.main",
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: mini ? 0 : "32px",
            color: "inherit",
          }}
        >
          {icon}
        </ListItemIcon>
        {mini ? (
          <MiniLabel>{name}</MiniLabel>
        ) : (
          <ListItemText
            primary={name}
            slotProps={{ primary: { variant: "body2", noWrap: true } }}
          />
        )}
      </ListItemButton>
    );
  }

  // NAV Button with Collapse
  function ListItemButtonCollapse(props) {
    const { icon, name, openState, handleClick, listItemButtons } = props;
    return (
      <>
        <ListItemButton
          // Collapsed, there is nowhere to show the children, so opening a
          // group has to widen the rail first.
          onClick={mini ? () => (expand(), handleClick()) : handleClick}
          sx={{
            ...(mini ? miniButtonSx : { px: 1, py: 0.5 }),
            color: isDarkTheme ? "white" : darkGrey,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: mini ? 0 : "32px",
              color: "inherit",
            }}
          >
            {icon}
          </ListItemIcon>
          {mini ? (
            <MiniLabel>{name}</MiniLabel>
          ) : (
            <>
              <ListItemText
                primary={name}
                slotProps={{ primary: { variant: "body2", noWrap: true } }}
              />
              {openState ? <ExpandLess /> : <ExpandMore />}
            </>
          )}
        </ListItemButton>

        <Collapse in={openState && !mini} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {listItemButtons.map((listItemBtn) => {
              return (
                <RouterListItemButton
                  icon={listItemBtn.icon}
                  name={listItemBtn.name}
                  to={listItemBtn.to}
                  sxStyle={{
                    pl: 3,
                  }}
                  key={listItemBtn.name}
                />
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <Paper
      className="Sidebar"
      sx={{
        position: {
          xs: "fixed",
          md: "sticky",
        },
        transform: {
          xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
          md: "none",
        },
        transition: "transform 0.4s, width 0.4s",
        zIndex: 900,
        height: "100dvh",
        width: "var(--Sidebar-width)",
        overflowX: "hidden",
        top: 0,
        pb: 2,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <GlobalStyles
        styles={{
          ":root": {
            "--Sidebar-width": `${mini ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH}px`,
          },
        }}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: "fixed",
          zIndex: 9998,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: "var(--SideNavigation-slideIn)",
          transition: "opacity 0.4s",
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
            lg: "translateX(-100%)",
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Toolbar />
      <Box
        sx={{
          minHeight: 0,
          overflow: "hidden auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List sx={{ width: "100%", maxWidth: 360 }} component="nav">
          <RouterListItemButton
            icon={<HomeRoundedIcon />}
            name={"Home"}
            to="/"
          />

          <ComponentAuthorisation allowedRoles={["alloworders"]}>
            <RouterListItemButton
              icon={<AddShoppingCartIcon />}
              name={"New Order"}
              to={draftEntryPath}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["staffRequestCreate"]}>
            <RouterListItemButton
              icon={<AddShoppingCartIcon />}
              name={"New Request"}
              to={"/request"}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["alloworders"]}>
            <ListItemButtonCollapse
              icon={<ShoppingCartRoundedIcon />}
              name={"Orders"}
              openState={openOrders}
              handleClick={handleClickOrders}
              listItemButtons={[
                {
                  icon: <AddShoppingCartIcon />,
                  name: "New Order",
                  to: draftEntryPath,
                },
                {
                  icon: <ListIcon />,
                  name: "Draft Orders",
                  to: "/draft-orders",
                },
                {
                  icon: <CheckBoxOutlineBlankIcon />,
                  name: "Received Orders",
                  to: "received-orders",
                },
                {
                  icon: <CheckBoxIcon />,
                  name: "Completed Orders",
                  to: "completed-orders",
                },
              ]}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["staffRequestApprove"]}>
            <ListItemButtonCollapse
              icon={<ChecklistIcon />}
              name={"Approvals"}
              openState={openApprovals}
              handleClick={handleClickApprovals}
              listItemButtons={[
                {
                  icon: <ChecklistIcon />,
                  name: "Pending Approval",
                  to: "/approvals",
                },
                {
                  icon: <CheckBoxOutlineBlankIcon />,
                  name: "Approved Requests",
                  to: "/approved-orders",
                },
                {
                  icon: <CheckBoxIcon />,
                  name: "Completed Requests",
                  to: "/approved-invoices",
                },
              ]}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["staffRequestCreate"]}>
            <ListItemButtonCollapse
              icon={<ShoppingCartRoundedIcon />}
              name={"Requests"}
              openState={openRequests}
              handleClick={handleClickRequests}
              listItemButtons={[
                {
                  icon: <AddShoppingCartIcon />,
                  name: "New Request",
                  to: "/request",
                },
                {
                  icon: <ListIcon />,
                  name: "Draft Requests",
                  to: "/draft-requests",
                },
                {
                  icon: <CheckBoxOutlineBlankIcon />,
                  name: "Submitted Requests",
                  to: "/submitted-requests",
                },
                {
                  icon: <CheckBoxIcon />,
                  name: "Request History",
                  to: "/request-history",
                },
              ]}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["allowstaff"]}>
            <ListItemButtonCollapse
              icon={<PeopleIcon />}
              name={"Staff"}
              openState={openStaff}
              handleClick={handleClickStaff}
              listItemButtons={[
                canCreateStaff && {
                  icon: <PersonAddIcon />,
                  name: "Add Staff",
                  to: "/staff/add",
                },
                {
                  icon: <GroupsIcon />,
                  name: "All Staff",
                  to: "/staff",
                },
                {
                  icon: <PeopleIcon />,
                  name: "Active Staff",
                  to: "/staff/active",
                },
                {
                  icon: <PeopleOutlineIcon />,
                  name: "Inactive Staff",
                  to: "/staff/inactive",
                },
              ].filter(Boolean)}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["allowwardrobe"]}>
            <ListItemButtonCollapse
              icon={<CheckroomIcon />}
              name={"Wardrobes"}
              openState={openWardrobe}
              handleClick={handleClickWardrobe}
              listItemButtons={[
                {
                  icon: <PlaylistAddIcon />,
                  name: "Wardrobe Request",
                  to: "/wardrobe-request",
                },
                {
                  icon: <ListIcon />,
                  name: "All Items",
                  to: "/items",
                },
                {
                  icon: <CheckroomIcon />,
                  name: "All Wardrobes",
                  to: "/wardrobes",
                },
              ]}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["allowentitlement"]}>
            <ListItemButtonCollapse
              icon={<RefreshIcon />}
              name={"Entitlement"}
              openState={openEntitlement}
              handleClick={handleClickEntitlement}
              listItemButtons={[
                {
                  icon: <RefreshIcon />,
                  name: "Entitlement Remaining",
                  to: "/entitlement/remaining",
                },
                {
                  icon: <PublishedWithChangesIcon />,
                  name: "Entitlement Complete",
                  to: "/entitlement/complete",
                },
                {
                  icon: <SyncProblemIcon />,
                  name: "Entitlement Exceeded",
                  to: "/entitlement/exceeded",
                },
                {
                  icon: <EventRepeatIcon />,
                  name: "Predicted Entitlement",
                  to: "/entitlement/predicted",
                },
              ]}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["allowreturns"]}>
            <ListItemButtonCollapse
              icon={<UndoIcon />}
              name={"Returns"}
              openState={openReturns}
              handleClick={handleClickReturns}
              listItemButtons={[
                {
                  icon: <UndoIcon />,
                  name: "Start Return",
                  to: "/returns/new",
                },
                {
                  icon: <SubjectIcon />,
                  name: "Previous Orders",
                  to: "/completed-orders",
                },
                {
                  icon: <CheckBoxOutlineBlankIcon />,
                  name: "Received Returns",
                  to: "/returns/pending",
                },
                {
                  icon: <CheckBoxIcon />,
                  name: "Completed Returns",
                  to: "/returns/completed",
                },
              ]}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation allowedRoles={["allowfinances"]}>
            <ListItemButtonCollapse
              icon={<CreditCardIcon />}
              name={"Finance"}
              openState={openFinance}
              handleClick={handleClickFinance}
              listItemButtons={[
                {
                  icon: <AccountBalanceWalletIcon />,
                  name: "Account Balance",
                  to: "/balance",
                },
                {
                  icon: <DescriptionIcon />,
                  name: "Outstanding Invoices",
                  to: "/invoices/outstanding",
                },
                {
                  icon: <DescriptionIcon />,
                  name: "Settled Invoices",
                  to: "/invoices/settled",
                },
                {
                  icon: <DescriptionIcon />,
                  name: "Outstanding Cr. Notes",
                  to: "/credit/outstanding",
                },
                {
                  icon: <DescriptionIcon />,
                  name: "Settled Cr. Notes",
                  to: "/credit/settled",
                },
              ]}
            />
          </ComponentAuthorisation>

          <ComponentAuthorisation
            allowedRoles={["allowgeneralreports", "allowfinancialreports"]}
          >
            <ListItemButtonCollapse
              icon={<StackedBarChartIcon />}
              name={"Reports"}
              openState={openReports}
              handleClick={handleClickReports}
              listItemButtons={[
                {
                  icon: <DataUsageIcon />,
                  name: "General Reports",
                  to: "/reports/general",
                },
                {
                  icon: <SsidChartIcon />,
                  name: "Financial Reports",
                  to: "/reports/financial",
                },
              ]}
            />
          </ComponentAuthorisation>
        </List>

        <List
          sx={{
            width: "100%",
            maxWidth: 360,
            mt: "auto",
            flexGrow: 0,
            mb: 0,
          }}
          component="nav"
          size="sm"
        >
          <RouterListItemButton
            icon={<SupportRoundedIcon />}
            name={"Support"}
            to={"/support"}
          />
        </List>
      </Box>
    </Paper>
  );
}
